import styled from '@emotion/styled';
import Pencil from '../../svgs/Pencil';
import { css } from '@emotion/react';
import { useRef } from 'react';
import {
  useIsContextMenuVisible,
  useIsParentVisible,
} from './hooks/InlineEditContextMenuHooks';

export const buildEditButton = (onClick: () => void) => ({
  icon: <Pencil />,
  label: 'Edit',
  onClick,
});

const InlineEditContextMenuWrapper = styled('div')`
  position: absolute;
  bottom: 0;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const BelowContainer = (
  outlineOffset: number = 0,
  isFloating: boolean = false
) => css`
  position: ${isFloating ? 'fixed' : 'absolute'};
  ${isFloating ? 'bottom: 8px;' : ''}
  ${isFloating ? 'z-index: 10;' : ''}
  padding-top: 8px;
  margin-top: ${outlineOffset}px;
`;

const InsideContainer = (outlineOffset: number = 0) => css`
  padding-bottom: 20px;
  margin-bottom: ${outlineOffset}px;
`;

interface InlineEditContextMenuContainerProps {
  outlineOffset?: number;
  position?: 'below' | 'inside';
  isFloating?: boolean;
}

const InlineEditContextMenuContainer = styled(
  'div'
)<InlineEditContextMenuContainerProps>`
  ${({ position, outlineOffset, isFloating }) =>
    position === 'below'
      ? BelowContainer(outlineOffset, isFloating)
      : InsideContainer(outlineOffset)}
`;

const InlineEditContextMenuButtonBar = styled('div')`
  display: flex;
  gap: 12px;
`;

const InlineEditContextMenuButton = styled('button')`
  border-radius: 25px;
  border: 1px solid #e4e5e9;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2px;
  min-width: 40px;
  min-height: 40px;
  z-index: 10;
  filter: drop-shadow(0 5px 12px rgba(0, 0, 0, 0.16))
    drop-shadow(0 3px 12px rgba(0, 0, 0, 0.14));
`;

const InlineEditContextMenuButtonIconWrapper = styled('div')`
  height: 18px;
  width: 18px;
`;

export interface InlineEditContextMenuButton {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

interface InlineEditContextMenuProps {
  buttons: InlineEditContextMenuButton[];
  outlineOffset?: number;
  position?: 'below' | 'inside';
  parentRef: React.RefObject<HTMLElement | null>;
}

const InlineEditContextMenu = ({
  buttons,
  outlineOffset = 0,
  position = 'below',
  parentRef,
}: InlineEditContextMenuProps) => {
  const instanceRef = useRef<HTMLDivElement>(null);

  const isContextMenuVisible = useIsContextMenuVisible(instanceRef);
  const isParentVisible = useIsParentVisible(parentRef);
  const shouldRenderFloatingContextMenu =
    isContextMenuVisible && isParentVisible;

  return (
    <InlineEditContextMenuWrapper ref={instanceRef}>
      <InlineEditContextMenuContainer
        outlineOffset={outlineOffset}
        position={position}
        isFloating={shouldRenderFloatingContextMenu}
      >
        <InlineEditContextMenuButtonBar>
          {buttons.map((button) => (
            <InlineEditContextMenuButton
              key={button.label}
              onClick={button.onClick}
              data-testid={`InlineEditContextMenu__Button__${button.label}`}
              aria-label={button.label}
            >
              <InlineEditContextMenuButtonIconWrapper>
                {button.icon}
              </InlineEditContextMenuButtonIconWrapper>
            </InlineEditContextMenuButton>
          ))}
        </InlineEditContextMenuButtonBar>
      </InlineEditContextMenuContainer>
    </InlineEditContextMenuWrapper>
  );
};

export default InlineEditContextMenu;
